import type { HTMLAttributes, ReactElement, ReactNode } from "react";
import styles from "./EmptyState.module.css";

export interface EmptyStateProps extends HTMLAttributes<HTMLDivElement> {
  /** One line naming the situation ("No documents yet"). */
  title: string;
  /** One or two sentences explaining WHY it is empty and what happens next. */
  description?: ReactNode;
  /** Optional single action. This app is read-only — usually a retry, never a create. */
  action?: ReactNode;
}

/**
 * The zero-rows / nothing-here state.
 *
 * An empty table with no explanation reads as a broken page. An empty state
 * says which thing is empty and why, so the operator can tell "no data yet"
 * apart from "the request failed" — the distinction that makes a read-only
 * dashboard trustworthy.
 */
export function EmptyState({
  title,
  description,
  action,
  className,
  ...rest
}: EmptyStateProps): ReactElement {
  const classes = [styles.emptyState, className].filter(Boolean).join(" ");
  return (
    <div className={classes} {...rest}>
      <p className={styles.title}>{title}</p>
      {description !== undefined ? <p className={styles.description}>{description}</p> : null}
      {action !== undefined ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
